// src/components/roads/page/RoadDebugDrawer.js
// Extracted from RoadWorksPage v4.7 — developer debug drawer (runtime state + log stream).
// --- UPDATE: v5.0 — owns its own log subscription + filter state so log traffic no longer
//             re-renders the whole page; level counts; text search; revokes export blob URL.
import React, { memo, useEffect, useMemo, useState } from 'react';
import {
    Box, Badge, Button, Divider, Drawer, DrawerBody, DrawerCloseButton, DrawerContent,
    DrawerFooter, DrawerHeader, DrawerOverlay, Flex, Grid, Heading, HStack, Icon, Input,
    Select, Text, VStack, useColorModeValue,
} from '@chakra-ui/react';
import { MdBugReport, MdDownload } from 'react-icons/md';

export const DebugLogRow = memo(({ log }) => {
    const rowBg = useColorModeValue('gray.100','gray.700');
    return (
        <Box p={2} bg={rowBg} borderRadius="md" fontSize="xs" fontFamily="mono">
            <Flex justify="space-between" mb={1}>
                <Badge colorScheme={
                    log.level==='error'   ? 'red'    :
                    log.level==='warn'    ? 'orange' :
                    log.level==='success' ? 'green'  :
                    log.level==='info'    ? 'blue'   : 'gray'
                } fontSize="10px">{log.level.toUpperCase()}</Badge>
                <Text color="gray.500" fontSize="10px">
                    {new Date(log.timestamp).toLocaleTimeString()}
                </Text>
            </Flex>
            <Text fontWeight="600">{log.message}</Text>
            {log.data && (
                <Text color="gray.500" fontSize="10px" mt={0.5}>
                    {(() => { try { return JSON.stringify(log.data).substring(0,120); } catch { return '[unserialisable]'; } })()}
                </Text>
            )}
        </Box>
    );
});
DebugLogRow.displayName = 'DebugLogRow';

const LEVELS = ['all','debug','info','warn','error','success'];

/**
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {DebugLogger} logger
 * @param {() => object} getRuntimeState  evaluated only while the drawer is open
 */
const RoadDebugDrawer = ({ isOpen, onClose, logger, getRuntimeState }) => {
    const mutedColor    = useColorModeValue('gray.500','gray.400');
    const borderColor   = useColorModeValue('gray.200','gray.700');
    const debugVStackBg = useColorModeValue('gray.50','gray.900');

    const [debugLogs, setDebugLogs] = useState(() => logger.getLogs().slice(-100));
    const [debugFilter, setDebugFilter] = useState('all');
    const [query, setQuery] = useState('');

    useEffect(() => {
        const unsub = logger.subscribe(entry =>
            setDebugLogs(prev => [...prev.slice(-99), entry])
        );
        return unsub;
    }, [logger]);

    const counts = useMemo(() => debugLogs.reduce((acc, l) => {
        acc[l.level] = (acc[l.level] || 0) + 1; return acc;
    }, {}), [debugLogs]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return (debugFilter === 'all' ? debugLogs : debugLogs.filter(d => d.level === debugFilter))
            .filter(d => !q || d.message.toLowerCase().includes(q))
            .slice(-50).reverse();
    }, [debugLogs, debugFilter, query]);

    const runtime = isOpen && getRuntimeState ? getRuntimeState() : {};

    const exportLogs = () => {
        const blob = new Blob([logger.export()], { type:'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `road-debug-${Date.now()}.json`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
            <DrawerOverlay backdropFilter="blur(4px)" />
            <DrawerContent>
                <DrawerCloseButton />
                <DrawerHeader borderBottomWidth="1px">
                    <HStack>
                        <Icon as={MdBugReport} color="yellow.500" />
                        <Text>FlowSense MOW+ Debug Panel</Text>
                    </HStack>
                </DrawerHeader>
                <DrawerBody>
                    <VStack spacing={4} align="stretch">
                        <Box>
                            <Heading size="sm" mb={3}>Runtime State</Heading>
                            <Grid templateColumns="repeat(2, 1fr)" gap={2} fontSize="sm">
                                {Object.entries(runtime).map(([k,v]) => (
                                    <React.Fragment key={k}>
                                        <Text fontSize="sm" fontWeight="600" color={mutedColor} textTransform="capitalize">{k}:</Text>
                                        <Text fontSize="sm" fontFamily="mono">{String(v ?? '—')}</Text>
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>
                        <Divider />
                        <Box>
                            <Flex justify="space-between" align="center" mb={2}>
                                <Heading size="sm">Logs ({debugLogs.length})</Heading>
                                <HStack>
                                    <Select size="sm" value={debugFilter}
                                        onChange={e => setDebugFilter(e.target.value)}
                                        maxW="130px" borderRadius="md" aria-label="Log level">
                                        {LEVELS.map(l =>
                                            <option key={l} value={l}>
                                                {l}{l !== 'all' && counts[l] ? ` (${counts[l]})` : ''}
                                            </option>
                                        )}
                                    </Select>
                                    <Button size="sm" colorScheme="red" variant="ghost"
                                        onClick={() => { logger.clear(); setDebugLogs([]); }}>
                                        Clear
                                    </Button>
                                </HStack>
                            </Flex>
                            <Input size="sm" mb={3} borderRadius="md" placeholder="Filter messages…"
                                value={query} onChange={e => setQuery(e.target.value)} />
                            <VStack spacing={2} align="stretch" maxH="420px"
                                overflowY="auto" bg={debugVStackBg} p={3}
                                borderRadius="lg" borderWidth="1px"
                                borderColor={borderColor}>
                                {visible.map(log => <DebugLogRow key={log.id} log={log} />)}
                                {visible.length === 0 && (
                                    <Text fontSize="xs" color={mutedColor} textAlign="center" py={4}>No logs yet</Text>
                                )}
                            </VStack>
                        </Box>
                    </VStack>
                </DrawerBody>
                <DrawerFooter borderTopWidth="1px">
                    <Button size="sm" mr={3} leftIcon={<Icon as={MdDownload} />} onClick={exportLogs}>
                        Export Logs
                    </Button>
                    <Button size="sm" colorScheme="blue" onClick={onClose}>Close</Button>
                </DrawerFooter>
            </DrawerContent>
        </Drawer>
    );
};

export default memo(RoadDebugDrawer);
